/**
 * The command centre: what the business is worth this month, and whether the product is working.
 *
 * Two queries. `overview` is the money and the customer counts; `health` over 14 days is the
 * turns, the split between voice and text, and what has failed. Both come from the API's own
 * arithmetic — nothing on this screen is added up in the browser.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { request } from '../api/client';
import type { Health, Overview } from '../api/types';
import { duration, money, moment, monthName, num, percent } from '../components/format';
import { Failed, Loading, Metric, PageHead, Panel, TurnsChart } from '../components/ui';

export function Dashboard() {
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => request<Overview>('/api/v1/admin/overview'),
  });
  const health = useQuery({
    queryKey: ['health', 14, null],
    queryFn: () => request<Health>('/api/v1/admin/health?days=14'),
  });

  if (overview.isPending) return <Loading what="Reading the board" />;
  if (overview.error)
    return <Failed error={overview.error} fallback="Could not load the dashboard." />;
  const data = overview.data;

  return (
    <>
      <PageHead
        eyebrow="Command centre"
        title="Electra, this morning"
        note={
          <>
            {monthName(data.month)}, day {data.days_elapsed} of {data.days_in_month}. Read at{' '}
            {moment(data.generated_at)}.
          </>
        }
        actions={
          <Link className="button-quiet" to="/customers?new=1">
            New customer
          </Link>
        }
      />

      {data.unconfigured_fixed.length > 0 ? (
        <div className="notice">
          Spend and margin are withheld while a fixed bill has no figure entered:{' '}
          <strong>{data.unconfigured_fixed.join(', ')}</strong>. A missing invoice is not a free
          one, so the total is blank rather than wrong.
        </div>
      ) : null}

      <div className="cards">
        <Metric
          label="Revenue this month"
          value={money(data.revenue_projected_gbp)}
          note={`${money(data.revenue_month_to_date_gbp)} billed so far`}
        />
        <Metric
          label="Cost to serve"
          value={money(data.spend_projected_gbp)}
          note={`${money(data.spend_month_to_date_gbp)} spent so far`}
        />
        <Metric
          label="Margin"
          value={money(data.margin_projected_gbp)}
          note="Projected revenue less projected spend"
          {...(data.margin_projected_gbp !== null && Number(data.margin_projected_gbp) < 0
            ? { tone: 'bad' as const }
            : {})}
        />
        <Metric
          label="Customers"
          value={num(data.customers_total)}
          note={`${data.customers_active} active · ${data.customers_trialing} trialing · ${data.customers_suspended} suspended · ${data.customers_closed} closed`}
          {...(data.customers_suspended ? { tone: 'warn' as const } : {})}
        />
      </div>

      {health.data ? (
        <Pulse health={health.data} />
      ) : health.error ? (
        <Failed error={health.error} fallback="Could not read health." />
      ) : (
        <Loading what="Counting turns" />
      )}

      <Panel eyebrow="Needs attention" title="Last 24 hours" flush>
        {data.attention.length === 0 ? (
          <div className="panel-body muted">Nothing failed for anybody in the last day.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th className="numeric">Failed runs</th>
                <th className="numeric">Errors</th>
                <th>Last failure</th>
                <th>Most recent</th>
              </tr>
            </thead>
            <tbody>
              {data.attention.map((item) => (
                <tr key={item.organisation_id}>
                  <td>
                    <Link to={`/customers/${item.organisation_id}`}>{item.name}</Link>
                  </td>
                  <td className="numeric bad">{item.failed_runs || '—'}</td>
                  <td className="numeric bad">{item.error_events || '—'}</td>
                  <td>{moment(item.last_failure_at)}</td>
                  <td className="muted wrap" title={item.latest_summary ?? undefined}>
                    {item.latest_summary ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function Pulse({ health }: { health: Health }) {
  const failures = health.failures.slice(0, 5);
  const brokenTools = health.tools.filter((tool) => tool.failed > 0).slice(0, 5);
  return (
    <>
      <div className="cards">
        <Metric
          label="Turns, 14 days"
          value={num(health.turns_total)}
          note={`${percent(health.turns_voice, health.turns_total)} voice · ${percent(health.turns_chat, health.turns_total)} text · ${num(health.turns_night_shift)} night shift`}
        />
        <Metric
          label="Failed turns"
          value={num(health.turns_failed)}
          note={`${percent(health.turns_failed, health.turns_total)} of all turns`}
          {...(health.turns_failed ? { tone: 'bad' as const } : {})}
        />
        <Metric
          label="Answer time"
          value={duration(health.latency.median_ms)}
          note={`p95 ${duration(health.latency.p95_ms)} · voice ${duration(health.latency_voice.median_ms)}`}
        />
        <Metric
          label="Voice heard"
          value={`${health.voice_minutes_heard} min`}
          note={`${num(health.voice_characters_spoken)} characters spoken back`}
        />
      </div>

      <div className="panel-grid" style={{ marginBottom: 22 }}>
        <Panel
          eyebrow="Pulse"
          title="Turns a day, by how they were said"
          actions={
            <div className="legend">
              <span>
                <i /> Text
              </span>
              <span>
                <i className="voice" /> Voice
              </span>
              <span>
                <i className="night" /> Night shift
              </span>
              <span>
                <i className="failed" /> Failed
              </span>
            </div>
          }
        >
          <TurnsChart
            days={health.days.map((d) => ({
              day: d.day,
              chat: d.chat_turns,
              voice: d.voice_turns,
              night: d.night_shift_runs,
              failed: d.failed_turns,
            }))}
          />
        </Panel>

        <Panel
          eyebrow="What is not working"
          title={failures.length || brokenTools.length ? 'Recurring failures' : 'Nothing recurring'}
          actions={
            <Link className="small" to="/health">
              Full health →
            </Link>
          }
        >
          {failures.length === 0 && brokenTools.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              No server error and no failing tool in the last fourteen days.
            </p>
          ) : (
            <div className="stack">
              {failures.map((group) => (
                <div
                  key={group.fingerprint}
                  className="row"
                  style={{ justifyContent: 'space-between' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div>
                      <span className="bad">{group.exception_type}</span>{' '}
                      <code className="muted">{group.path}</code>
                    </div>
                    <div className="small muted">{group.summary}</div>
                  </div>
                  <div className="numeric muted small" style={{ whiteSpace: 'nowrap' }}>
                    ×{group.count} · {moment(group.last_seen_at)}
                  </div>
                </div>
              ))}
              {brokenTools.map((tool) => (
                <div
                  key={tool.tool_name}
                  className="row"
                  style={{ justifyContent: 'space-between' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div>
                      <span className="warn">{tool.tool_name}</span>{' '}
                      <span className="muted small">tool</span>
                    </div>
                    <div className="small muted">
                      {tool.latest_failure ?? 'Failed without a summary'}
                    </div>
                  </div>
                  <div className="numeric muted small" style={{ whiteSpace: 'nowrap' }}>
                    {tool.failed}/{tool.calls} failed
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
